import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { PlusCircleIcon, XCircleIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';
import { formatCurrency } from '../Help/helper';
import { generateProcurementNumber } from '../Help/helper';
import Select from 'react-select';
import logger from '../../utils/logger';
import BreadCrumb from '../BreadCrumb';

const pages = [
  { name: 'Procurement', to: '/procurement', component: Link, current: false },
  { name: 'Requisition', to: '/procurement/buy-item', component: Link, current: true },
];

const Requisition = () => {
  const [customers, setCustomers] = useState([]);
  const navigate = useNavigate();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [formState, setFormState] = useState({
    customerName: '',
    category: '',
    supplier: '',
    grandTotal: 0,
    dateOfPurchase: '',
    paymentStatus: 'Unpaid',
    additionalNotes: '',
    status: 'Requested',
    statusUpdateDate: new Date().toISOString().split('T')[0],
  });

  const [cartItems, setCartItems] = useState([]);
  const [includeTax, setIncludeTax] = useState(false);

  useEffect(() => {
    const fetchSuppliers = async () => {
      const suppliersCollection = collection(db, 'suppliers');
      const suppliersSnapshot = await getDocs(suppliersCollection);
      const suppliersList = suppliersSnapshot.docs.map((doc) => doc.data());
      setSuppliers(suppliersList);
    };

    fetchSuppliers();
  }, []);

  useEffect(() => {
    const fetchCustomers = async () => {
      const querySnapshot = await getDocs(collection(db, 'customers'));
      const customersList = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return { value: doc.id, label: `${data.firstName} ${data.lastName}` };
      });
      setCustomers(customersList);
    };

    fetchCustomers();
  }, []);

  useEffect(() => {
    const procurementNumber = generateProcurementNumber();
    setFormState((prevState) => ({
      ...prevState,
      procurementNumber: procurementNumber,
    }));
  }, []);

  const handleCustomerChange = (selectedOption) => {
    setSelectedCustomer(selectedOption);
    setInvoices([]);
    setSelectedInvoice(null);
    setInvoiceDetails(null);
  };

  const fetchInvoicesForCustomer = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer first.');
      return;
    }

    const invoicesQuery = query(
      collection(db, 'newInvoices'),
      where('customerName', '==', selectedCustomer.label)
    );
    const querySnapshot = await getDocs(invoicesQuery);

    if (!querySnapshot.empty) {
      const invoicesList = querySnapshot.docs.map((doc) => {
        const invoiceData = doc.data();
        return {
          value: doc.id,
          label: invoiceData.invoiceNumber,
          jobCardNo: invoiceData.jobCardNo,
          invoiceNumber: invoiceData.invoiceNumber,
        };
      });
      setInvoices(invoicesList);
      toast.success(`${invoicesList.length} invoices fetched successfully.`);
    } else {
      toast.info('No invoices found for the selected customer.');
    }
  };

  const handleInvoiceChange = async (selectedOption) => {
    setSelectedInvoice(selectedOption);
    const invoiceDocRef = doc(db, 'newInvoices', selectedOption.value);
    const invoiceDoc = await getDoc(invoiceDocRef);

    if (invoiceDoc.exists()) {
      setInvoiceDetails(invoiceDoc.data());
    } else {
      toast.error('Failed to fetch invoice details.');
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let updatedValue = value;

    if (name === 'quantity' || name === 'unitPrice') {
      updatedValue = parseFloat(value) || 0;
    }

    setFormState((prevState) => ({
      ...prevState,
      [name]: updatedValue,
    }));
  };

  const supplierOptions = suppliers.map((supplier) => ({
    value: supplier.id,
    label: supplier.companyName,
  }));

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (cartItems.length === 0) {
      toast.error('Please add at least one item to the cart.');
      return;
    }

    const supplierRef = doc(db, 'suppliers', formState.supplier);
    const supplierSnap = await getDoc(supplierRef);

    if (!supplierSnap.exists()) {
      throw new Error('Supplier does not exist.');
    }

    const supplierName = supplierSnap.data().companyName;
    const subtotal = calculateCartTotal();
    const taxes = calculateTax();
    const grandTotal = calculateReceivable();

    try {
      const procurementData = {
        ...formState,
        supplier: supplierName,
        customerName: selectedCustomer ? selectedCustomer.label : '',
        jobCardNo: selectedInvoice ? selectedInvoice.jobCardNo : '',
        invoiceNumber: selectedInvoice ? selectedInvoice.invoiceNumber : '',
        paymentStatus: formState.paymentStatus || 'Unpaid',
        status: formState.status || 'Requested',
        dateRequired: formState.dateRequired || new Date().toISOString().split('T')[0],
        cartItems: cartItems.map((item) => ({
          description: item.description,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity, 10),
        })),
        subtotal,
        taxes,
        grandTotal,
      };

      await addDoc(collection(db, 'procurements'), procurementData);
      toast.success('Request Submitted successfully');
      navigate('/procurement/requests');
      resetFormAndSelectionStates();
    } catch (error) {
      toast.error('Error adding procurement: ' + error.message);
      logger.error(error);
    }
  };

  const resetFormAndSelectionStates = () => {
    setFormState({
      category: '',
      supplier: '',
      dateOfPurchase: '',
      paymentStatus: 'Unpaid',
      additionalNotes: '',
      status: 'Requested',
      statusUpdateDate: new Date().toISOString().split('T')[0],
    });
    setSelectedCustomer(null);
    setSelectedInvoice(null);
    setCartItems([]);
    setIncludeTax(false);
  };

  const handleCartItemChange = (index, field, value) => {
    const updatedCartItems = cartItems.map((item, i) => {
      if (i === index) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setCartItems(updatedCartItems);
  };

  const handleAddCartItem = () => {
    setCartItems([...cartItems, { description: '', price: '', quantity: 1 }]);
  };

  const handleRemoveCartItem = (index) => {
    const updatedCartItems = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedCartItems);
  };

  const calculateCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const calculateTax = () => {
    return includeTax ? calculateCartTotal() * 0.075 : 0;
  };

  const calculateReceivable = () => {
    return calculateCartTotal() + calculateTax();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Page Header */}
        <div className="mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl shadow-lg">
              <ShoppingCartIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900">Create Requisition</h1>
              <p className="text-sm text-gray-600 mt-1">
                Request items from registered vendors
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Left Side - Form Details */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Supplier <span className="text-red-500">*</span>
                    </label>
                    <Select
                      className="text-sm"
                      options={supplierOptions}
                      onChange={(selectedOption) => {
                        setFormState((prevState) => ({
                          ...prevState,
                          supplier: selectedOption.value,
                        }));
                      }}
                      placeholder="Select Supplier"
                      isSearchable={true}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Date Required <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm py-2 px-3"
                      name="dateRequired"
                      type="date"
                      value={formState.dateRequired}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm py-2 px-3"
                      name="category"
                      value={formState.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Category</option>
                      <option value="goods">Goods</option>
                      <option value="services">Services</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Planned Usage <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm py-2 px-3"
                      name="intendedUse"
                      value={formState.intendedUse}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Intended Use</option>
                      <option value="Office Consumable">Office Consumable</option>
                      <option value="Customer Use">Customer Use</option>
                    </select>
                  </div>

                  {formState.intendedUse === 'Customer Use' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Select Customer
                        </label>
                        <Select
                          options={customers}
                          onChange={handleCustomerChange}
                          placeholder="Select Customer"
                          isSearchable={true}
                          className="text-sm"
                        />

                        <button
                          type="button"
                          onClick={fetchInvoicesForCustomer}
                          className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 text-sm text-white px-3 py-1.5 rounded-xl mt-2 hover:from-green-600 hover:to-emerald-700 shadow-md transition-all duration-200"
                        >
                          <PlusCircleIcon className="h-4 w-4" />
                          <span className="text-sm font-semibold">Fetch Invoices</span>
                        </button>
                      </div>

                      {selectedCustomer && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Selected Invoice
                          </label>
                          <Select
                            options={invoices}
                            onChange={handleInvoiceChange}
                            placeholder="Select Invoice"
                            isSearchable={true}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                {invoiceDetails && (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-200">
                    <h3 className="font-bold text-gray-800 mb-2 text-sm uppercase">
                      Invoice Details
                    </h3>
                    <div className="space-y-1 text-xs">
                      <p>
                        <strong>Invoice Number:</strong> {invoiceDetails.invoiceNumber}
                      </p>
                      <p>
                        <strong>Job Card No:</strong> {invoiceDetails.jobCardNo}
                      </p>
                      <p>
                        <strong>Customer Name:</strong> {invoiceDetails.customerName}
                      </p>
                      <h4 className="mt-2 font-semibold">Items:</h4>
                      <ul className="list-disc ml-4">
                        {invoiceDetails.cartItems.map((item, index) => (
                          <li key={index}>
                            {item.description} - {item.quantity} @{' '}
                            {formatCurrency(item.price)}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-2">
                        <strong>Total Sale:</strong>{' '}
                        {formatCurrency(invoiceDetails.totalSale)}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    className="block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm py-2 px-3"
                    rows={4}
                    name="additionalNotes"
                    value={formState.additionalNotes}
                    onChange={handleInputChange}
                    placeholder="Write details about the nature of the purchase..."
                  />
                </div>
              </div>
            </div>

            {/* Right Side - Cart Items */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-bold text-gray-800 mb-3 text-sm uppercase">
                Detailed Breakdown of Vendor Costs
              </h3>

              <div className="space-y-2 max-h-96 overflow-y-auto mb-3">
                {cartItems.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2 pb-2 border-b">
                    <div className="flex-grow">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          handleCartItemChange(index, 'description', e.target.value)
                        }
                        placeholder="Description"
                        className="block w-full text-xs rounded-xl p-2 border-gray-300 shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div className="w-24">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
                          <span className="text-gray-500 text-xs">₦</span>
                        </div>
                        <input
                          type="text"
                          name="price"
                          value={item.price}
                          onChange={(e) =>
                            handleCartItemChange(index, 'price', e.target.value)
                          }
                          className="block w-full pl-6 pr-2 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="w-16">
                      <select
                        value={item.quantity}
                        onChange={(e) =>
                          handleCartItemChange(index, 'quantity', e.target.value)
                        }
                        className="block w-full p-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="" disabled>
                          Qty
                        </option>
                        {[...Array(100).keys()].map((num) => (
                          <option key={num + 1} value={num + 1}>
                            {num + 1}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveCartItem(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleAddCartItem}
                className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-sm text-white px-3 py-2 rounded-xl hover:from-blue-600 hover:to-cyan-700 shadow-md transition-all duration-200 font-semibold"
              >
                <PlusCircleIcon className="h-5 w-5" />
                <span>Add Item</span>
              </button>

              <div className="mt-4 space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeTax}
                    onChange={(e) => setIncludeTax(e.target.checked)}
                    className="form-checkbox rounded text-purple-600 shadow-sm focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Include 7.5% Tax</span>
                </label>

                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-200">
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">Subtotal:</span>
                      <span className="font-bold">{formatCurrency(calculateCartTotal())}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">VAT (7.5%):</span>
                      <span className="font-bold">{formatCurrency(calculateTax())}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-purple-200">
                      <span className="font-bold text-gray-900">Grand Total:</span>
                      <span className="text-xl font-black text-purple-600">
                        {formatCurrency(calculateReceivable())}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-bold shadow-lg transition-all duration-200"
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </form>

        <ToastContainer />
      </div>
    </div>
  );
};

export default Requisition;
